import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { useAuth } from "../../../Contexts/AuthContext";
import { useStablesBalances } from "../../../Hooks/useStablesBalances";
import { getMissingStablesCreateAssets, STABLES_WALLET_CREATED_EVENT } from "./constants";
import { StablesCreateWalletModal } from "./StablesCreateWalletModal";

const StablesWalletCreateContext = createContext({
  canCreateWallet: false,
  missingAssets: [],
  ownedCount: 0,
  openCreate: () => {},
});

export const useStablesWalletCreate = () => useContext(StablesWalletCreateContext);

/**
 * Ensures the active IB account can operate Stables and create missing asset wallets.
 * Empty state auto-opens create. After the first wallet exists, remaining catalog
 * assets stay creatable (cards / modal) instead of hiding the flow.
 */
export const StablesWalletGate = ({ children }) => {
  const { hasPermission } = useAuth();
  const canCreateWallet = hasPermission("stablesoperate");
  const { assets, loading, error, reload } = useStablesBalances();
  const ownedCount = assets?.length ?? 0;
  const hasWallet = ownedCount > 0;
  const missingAssets = useMemo(
    () => getMissingStablesCreateAssets(assets),
    [assets],
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [initialAsset, setInitialAsset] = useState(null);

  const openCreate = useCallback((asset) => {
    if (!canCreateWallet || missingAssets.length === 0) return;
    setInitialAsset(asset || null);
    setModalOpen(true);
  }, [canCreateWallet, missingAssets.length]);

  useEffect(() => {
    if (loading || error) return;
    if (!hasWallet && canCreateWallet && missingAssets.length > 0) {
      setInitialAsset(null);
      setModalOpen(true);
    }
  }, [loading, error, hasWallet, canCreateWallet, missingAssets.length]);

  const contextValue = useMemo(
    () => ({
      canCreateWallet,
      missingAssets,
      ownedCount,
      openCreate,
    }),
    [canCreateWallet, missingAssets, ownedCount, openCreate],
  );

  if (error) {
    return (
      <div className="app-card app-empty-state">
        <p>{typeof error === "string" ? error : intl.get("stables.balancesLoadError")}</p>
        <button type="button" className="btn-outline mt-2 !rounded-full !px-4" onClick={reload}>
          {intl.get("stables.retryLoad")}
        </button>
      </div>
    );
  }

  if (!loading && !hasWallet && !canCreateWallet) {
    return (
      <StablesWalletCreateContext.Provider value={contextValue}>
        <div className="app-card app-empty-state mb-5">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
            {intl.get("stables.noWallet.readOnlyTitle")}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {intl.get("stables.noWallet.readOnlySubtitle")}
          </p>
        </div>
        {children}
      </StablesWalletCreateContext.Provider>
    );
  }

  return (
    <StablesWalletCreateContext.Provider value={contextValue}>
      {children}
      {!loading && canCreateWallet && missingAssets.length > 0 ? (
        <StablesCreateWalletModal
          open={modalOpen}
          assets={missingAssets}
          initialAsset={initialAsset}
          onClose={() => {
            setModalOpen(false);
            setInitialAsset(null);
          }}
          onCreated={() => {
            setModalOpen(false);
            setInitialAsset(null);
            // Gate + BalanceCards each own a useStablesBalances instance — broadcast
            // so every listener reloads (same pattern as IB account switch).
            try {
              window.dispatchEvent(new CustomEvent(STABLES_WALLET_CREATED_EVENT));
            } catch {
              void reload();
            }
          }}
        />
      ) : null}
    </StablesWalletCreateContext.Provider>
  );
};

export default StablesWalletGate;
